import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { Empresa, EmpresaUsuario, EmpresasPorPuestoYZona } from '../objetos/empresa';
import { ParamsPaginacion, SimpleResponse, SimpleResponseGeneric } from '../objetos/simple-response';
import { aplicarPaginacion } from './paginacion-params';
import { contextoSinSpinner } from '../../../shared/services/interceptors/peticion-sin-spinner';
import { Usuario } from '../objetos/usuario';
import { ConexionesService } from '../../../shared/services/conexiones.service';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class EmpresasService {
  private baseUrl: string = '';
  constructor(
    private http: HttpClient,
    private conexionesService: ConexionesService
  ) {
    //Subscripción al cambio de conexion
    conexionesService.conexionesRO.subscribe((res) => {
      if (res && res.length > 0) {
        const apiNombre = environment.conexiones_nombre.auth;
        const conexion: any = conexionesService.buscarConexion(apiNombre);
        this.baseUrl = conexion?.URL;
      }
    });
  }

  obtenerEmpresasPublic = (
    esCedis: boolean | undefined = undefined,
    online: boolean | undefined = undefined,
    onix: boolean | undefined = undefined,
    zonaId: number | null | undefined = undefined,
    incluirInactivos: boolean = false,
    paginacion?: ParamsPaginacion | null,
    silencioso = false,
  ): Observable<SimpleResponse> => {
    let params = aplicarPaginacion(new HttpParams(), paginacion);
    if (incluirInactivos) {
      params = params.set('todos', 'true');
    }
    if (esCedis !== undefined) {
      params = params.set('esCedis', esCedis);
    }
    if (online !== undefined) {
      params = params.set('online', online);
    }
    if (onix !== undefined) {
      params = params.set('onix', onix);
    }
    if (zonaId !== undefined && zonaId !== null) {
      params = params.set('zonaId', zonaId);
    }
    return this.http
      .get(`${this.baseUrl}/api/empresas/public`, {
        context: silencioso ? contextoSinSpinner() : undefined,
        headers: {
          'x-auth': localStorage.getItem('token') || '',
        },
        params,
      })
      .pipe(tap((resp: any) => resp));
  };


  obtenerEmpresas = (fields: string[] | null): Observable<SimpleResponse> => {
    let params = new HttpParams();
    if (fields) {
      params = params.set('fields', fields.join(','));
    }
    return this.http
      .get(`${this.baseUrl}/api/empresas`, {
        headers: {
          'x-auth': localStorage.getItem('token') || '',
        },
        params,
      })
      .pipe(tap((resp: any) => resp));
  };

  obtenerEmpresasPorToken = (): Observable<SimpleResponse> => {
    return this.http
      .get(`${this.baseUrl}/api/empresas/obtenerEmpresasPorUsuario`, {
        headers: {
          'x-auth': localStorage.getItem('token') || '',
        },
      })
      .pipe(tap((resp: any) => resp));
  };

  obtenerEmpresasSinZonaAsignada = (): Observable<SimpleResponse> => {
    return this.http
      .get(`${this.baseUrl}/api/empresas/sin-zona-asignada`, {
        headers: {
          'x-auth': localStorage.getItem('token') || '',
        },
      })
      .pipe(tap((resp: any) => resp));
  };

  obtenerEmpresaId = (empresa: Empresa): Observable<SimpleResponse> => {
    return this.http
      .get(`${this.baseUrl}/api/empresas/${empresa.id}`, {
        headers: {
          'x-auth': localStorage.getItem('token') || '',
        },
      })
      .pipe(tap((resp: any) => resp));
  };

  desactivarEmpresa = (empresa: Empresa): Observable<SimpleResponse> => {
    const url = `${this.baseUrl}/api/empresas/${empresa.id}`;
    return this.http
      .delete(url, {
        headers: {
          'x-auth': localStorage.getItem('token') || '',
        },
      })
      .pipe(tap((resp: any) => resp));
  };

  crearEmpresa = (empresa: Empresa): Observable<SimpleResponse> => {
    const url = `${this.baseUrl}/api/empresas`;
    return this.http
      .post(url, empresa, {
        headers: {
          'x-auth': localStorage.getItem('token') || '',
        },
      })
      .pipe(tap((resp: any) => resp));
  };

  editarEmpresa = (empresa: Empresa): Observable<SimpleResponse> => {
    const url = `${this.baseUrl}/api/empresas/${empresa.id}`;
    return this.http
      .put(url, empresa, {
        headers: {
          'x-auth': localStorage.getItem('token') || '',
        },
      })
      .pipe(tap((resp: any) => resp));
  };

  obtenerEmpresasPorUsuario = (
    usuario: Usuario
  ): Observable<SimpleResponse> => {
    return this.http
      .get(
        `${this.baseUrl}/api/empresas/obtener-empresas-usuario-id/${usuario.id}`,
        {
          headers: {
            'x-auth': localStorage.getItem('token') || '',
          },
        }
      )
      .pipe(tap((resp: any) => resp));
  };

  asignarEmpresasUsuario = (
    empresas: Empresa[],
    usuario: Usuario
  ): Observable<SimpleResponse> => {
    return this.http
      .post(
        `${this.baseUrl}/api/empresas/asignar-empresas-usuario`,
        {
          empresas,
          id: usuario.id,
        },
        {
          headers: {
            'x-auth': localStorage.getItem('token') || '',
          },
        }
      )
      .pipe(tap((resp: any) => resp));
  };

  obtenerSucursalesMicrosip = (): Observable<SimpleResponse> => {
    return this.http
      .get(`${this.baseUrl}/api/empresas/microsip/`, {
        headers: {
          'x-auth': localStorage.getItem('token') || '',
        },
      })
      .pipe(tap((resp: any) => resp));
  };

  obtenerSucursalesPorUsuarioMicrosip = (
    usuarioMS: any
  ): Observable<SimpleResponse> => {
    return this.http
      .get(`${this.baseUrl}/api/empresas/microsip/${usuarioMS}`, {
        headers: {
          'x-auth': localStorage.getItem('token') || '',
        },
      })
      .pipe(tap((resp: any) => resp));
  };
  obtenerCajeros = (
    sucursalId: number,
    usuarioId: number,
    empresaId: number
  ): Observable<SimpleResponse> => {
    const url = `${this.baseUrl}/api/cajeros/microsip/${sucursalId}/${usuarioId}/${empresaId}`;

    return this.http
      .get(url, { headers: { 'x-auth': localStorage.getItem('token') || '' } })
      .pipe(tap((resp: any) => resp));
  };

  obtenerEmpresaMicrosipPorId = (
    sucursalId: number
  ): Observable<SimpleResponse> => {
    const url = `${this.baseUrl}/api/empresas/${sucursalId}/empresa_microsip`;
    return this.http
      .get(url, { headers: { 'x-auth': localStorage.getItem('token') || '' } })
      .pipe(tap((resp: any) => resp));
  };

  obtenerAlmacenDefectoSucursal = (
    sucursalId: number | undefined,
    idOpcion: number | undefined
  ): Observable<SimpleResponse> => {
    const url = `${this.baseUrl}/api/relaciones/almacen`;
    let params: any = {};
    if(sucursalId) params.sucursalID = sucursalId;
    if(idOpcion) params.idOpcion = idOpcion;
    params.entrada = 'true';

    return this.http
      .get(url, {
        headers: { 'x-auth': localStorage.getItem('token') || '' },
        params
      })
      .pipe(tap((resp: any) => resp));
  };

  obtenerRelacionSucursal = (
    esCedis: boolean,
    empresaSeleccionadaId: number,
    suministro: boolean = false,
  ): Observable<SimpleResponseGeneric<EmpresaUsuario[] | EmpresaUsuario>> => {
    const url = `${this.baseUrl}/api/relaciones/sucursal`;
    const relacion = esCedis ? 'hijos' : 'padre';
    const res = this.http
      .get(url, {
        headers: { 'x-auth': localStorage.getItem('token') || '' },
        params: { relacion, empresaSeleccionadaId, suministro},
      })
      .pipe(tap((resp: any) => resp));
    if (esCedis) {
      return res as Observable<SimpleResponseGeneric<EmpresaUsuario[]>>;
    }else {
      return res as Observable<SimpleResponseGeneric<EmpresaUsuario>>;
    }
  }

  obtenerCedisDeSucursal = (
    sucursalIdHijo: number
  ): Observable<SimpleResponse> => {
    const url = `${this.baseUrl}/api/relaciones/empresa`;
    return this.http
      .get(url, {
        headers: { 'x-auth': localStorage.getItem('token') || '' },
        params: { sucursalIdHijo },
      })
      .pipe(tap((resp: any) => resp));
  };

  sincronizarCedisSucursal = (
    sucursalIdHijo: number,
    cedisIds: number[],
    cedisSuministrosId: number | null = null
  ): Observable<SimpleResponse> => {
    const url = `${this.baseUrl}/api/relaciones/empresa/sucursal/${sucursalIdHijo}`;
    return this.http
      .put(
        url,
        { cedisIds, cedisSuministrosId },
        { headers: { 'x-auth': localStorage.getItem('token') || '' } }
      )
      .pipe(tap((resp: any) => resp));
  };

  obtenerEmpresasPorPuestoyZona = (
    usuarioId?: number
  ): Observable<SimpleResponseGeneric<EmpresasPorPuestoYZona>> => {
    const url = `${this.baseUrl}/api/empresas/puesto/zona`;
    let params: any = {};
    if(usuarioId) params.usuarioId = usuarioId;
    
    return this.http
      .get(url, {
        headers: { 'x-auth': localStorage.getItem('token') || '' },
        params
      })
      .pipe(tap((resp: any) => resp));
  }
}
